<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { developmentLogin, listMyShops } from '../api';
import { formatBeijingDateTime } from '../utils/datetime';

const router = useRouter();
const shops = ref<Array<Record<string, unknown>>>([]);
const loading = ref(true);
function text(value: unknown, fallback: string): string { return typeof value === 'string' && value.trim() ? value : fallback; }
function savedAt(value: unknown): string { return `最近保存：${formatBeijingDateTime(value, '保存时间待同步')}`; }
function reviewReason(shop: Record<string, unknown>): string { return shop.status === 'NEEDS_SUPPLEMENT' ? text(shop.returnReason, '请按审核要求补充资料') : shop.status === 'REJECTED' ? text(shop.rejectionReason, '请联系平台工作人员了解详情') : ''; }
function shopAddressLabel(shop: Record<string, unknown>): string { return `${text(shop.shopArea, '')}${text(shop.shopAddress, '')}` || '尚未填写地址'; }
function openShop(shop: Record<string, unknown>): void { router.push(shop.status === 'DRAFT' || shop.status === 'NEEDS_SUPPLEMENT' ? `/shops/${shop.id}/edit` : `/shops/${shop.id}`); }

onMounted(async () => {
  try { await developmentLogin(); shops.value = await listMyShops(); } finally { loading.value = false; }
});
</script>

<template>
  <section class="h5-page">
    <van-nav-bar title="我的门店" left-arrow @click-left="router.push('/')" />
    <van-loading v-if="loading" vertical style="padding-top: 72px">正在加载门店</van-loading>
    <van-empty v-else-if="shops.length === 0" description="暂无门店信息">
      <van-button type="primary" round @click="router.push('/shops/new')">去上报门店</van-button>
    </van-empty>
    <div v-else class="my-shops-content">
      <div class="shop-card-list">
        <article v-for="shop in shops" :key="String(shop.id)" class="shop-card">
          <section class="shop-card__main" @click="openShop(shop)">
            <header class="shop-card__header"><strong class="shop-card__name">{{ text(shop.shopName, '未填写门店名称') }}</strong><div class="shop-list-actions"><van-tag :type="shop.status === 'APPROVED' ? 'success' : shop.status === 'REJECTED' ? 'danger' : 'warning'">{{ shop.statusLabel }}</van-tag><van-button v-if="shop.status !== 'DRAFT' && shop.status !== 'NEEDS_SUPPLEMENT'" size="mini" plain type="primary" @click.stop="router.push(`/shops/${shop.id}`)">查看资料</van-button><van-icon name="arrow" /></div></header>
            <div class="shop-card__address">{{ shopAddressLabel(shop) }}</div>
            <div class="shop-card__saved-at">{{ savedAt(shop.lastSavedAt) }}</div>
          </section>
          <section v-if="shop.status === 'NEEDS_SUPPLEMENT' || shop.status === 'REJECTED'" class="shop-review-feedback" :class="shop.status === 'REJECTED' ? 'shop-review-feedback--rejected' : 'shop-review-feedback--supplement'">
            <van-icon :name="shop.status === 'REJECTED' ? 'clear' : 'warning-o'" />
            <div class="shop-review-feedback__content"><strong>{{ shop.status === 'REJECTED' ? '审核未通过' : '需要补充资料' }}</strong><p><b>{{ shop.status === 'REJECTED' ? '拒绝理由：' : '审核意见：' }}</b>{{ reviewReason(shop) }}</p></div>
            <van-button v-if="shop.status === 'NEEDS_SUPPLEMENT'" size="small" type="warning" @click="openShop(shop)">去补充</van-button>
            <van-button v-else size="small" plain type="danger" @click="openShop(shop)">查看资料</van-button>
          </section>
        </article>
      </div>
      <div class="report-shop-action">
        <van-button block type="primary" @click="router.push('/shops/new')">门店上报</van-button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.shop-list-actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; }
.my-shops-content { padding-bottom: 24px; }
.report-shop-action { margin: 16px; }
.shop-card-list { display: grid; gap: 12px; margin: 16px; }
.shop-card { overflow: hidden; border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(20, 42, 74, .06); }
.shop-card__main { padding: 14px 16px; }
.shop-card__header { display: flex; align-items: center; gap: 12px; min-width: 0; }
.shop-card__name { overflow: hidden; flex: 1; color: var(--van-cell-text-color); font-size: 16px; line-height: 24px; text-overflow: ellipsis; white-space: nowrap; }
.shop-card__address { overflow: hidden; margin-top: 5px; color: var(--van-cell-label-color); font-size: var(--van-cell-label-font-size); line-height: 18px; text-overflow: ellipsis; white-space: nowrap; }
.shop-card__saved-at { overflow: hidden; margin-top: 4px; color: #9aa3b2; font-size: 12px; line-height: 18px; text-overflow: ellipsis; white-space: nowrap; }
.shop-review-feedback { display: flex; align-items: flex-start; gap: 8px; padding: 11px 12px; border-top: 1px solid #ffe7ba; background: #fffaf0; color: #ad6800; }
.shop-review-feedback > :deep(.van-icon) { flex: none; margin-top: 2px; font-size: 18px; }
.shop-review-feedback__content { min-width: 0; flex: 1; }
.shop-review-feedback__content strong { display: block; font-size: 13px; line-height: 19px; }
.shop-review-feedback__content p { margin: 2px 0 0; color: #8c6b36; font-size: 12px; line-height: 18px; word-break: break-word; }
.shop-review-feedback__content p b { color: #ad6800; font-weight: 600; }
.shop-review-feedback--rejected { border-top-color: #ffccc7; background: #fff2f0; color: #cf1322; }
.shop-review-feedback--rejected .shop-review-feedback__content p { color: #9f4045; }
.shop-review-feedback--rejected .shop-review-feedback__content p b { color: #cf1322; }
</style>
